import { GetStaticProps, NextPage } from 'next';
import { Container, Row, Col } from 'reactstrap';
import { RichText } from 'prismic-reactjs';
import { Client } from '../utils';
import { Layout, SeoMeta } from '../components/Layout';
import { ImageComponent } from '../components/Media';
import { Anchor } from '../components/Anchor/anchor';
import { Section } from '../components/Sections';
import { Slider } from '../components/Slider';
import { Button } from '../components/Buttons';
import { MapBoxSection } from '../components/Mapbox';
import { LinkTree } from '../components/Linktree';
import { TextContainer } from '../components/Text';

interface Props {
  all?: any;
  content: any;
  map: {
    map_restaurants: Array<MapItem>;
    map_bars: Array<MapItem>;
    map_shops: Array<MapItem>;
    map_fitness: Array<MapItem>;
    map_grocery: Array<MapItem>;
    map_transportation: Array<MapItem>;
  };
}

interface MapItem {
  name: any;
  address: any;
  geopoint: {
    latitude: number;
    longitude: number;
  };
}

const Neighborhood: NextPage<Props> = ({ content, map }) => {
  return (
    <Layout>
      <SeoMeta
        title={RichText.asText(content.seo__title)}
        desc={
          content.seo__description && RichText.asText(content.seo__description)
        }
        socialImage={content.seo__image}
      />

      {content.slider && content.slider.length > 0 && (
        <Slider slides={content.slider} />
      )}
      {((content.intro__headline &&
        RichText.asText(content.intro__headline) !== '') ||
        (content.intro__text__left &&
          RichText.asText(content.intro__text__left) !== '') ||
        (content.intro__text__right &&
          RichText.asText(content.intro__text__right) !== '')) && (
        <Section hasTop hasBottom={false}>
          <Container fluid>
            <Row>
              {RichText.asText(content.intro__headline) !== '' && (
                <Col md={12} lg={3}>
                  <h1 className="font--xxl font--primary prevent-line-break">
                    A story <br />
                    to tell
                  </h1>
                </Col>
              )}
              {(RichText.asText(content.intro__text__left) !== '' ||
                RichText.asText(content.intro__text__right) !== '') && (
                <Col
                  md={{
                    size: 12,
                    offset: 0,
                  }}
                  lg={{
                    size: 8,
                    offset: 1,
                  }}
                >
                  <TextContainer>
                    <Row>
                      {content.intro__text__left &&
                        RichText.asText(content.intro__text__left) !== '' && (
                          <Col sm={6}>
                            <p className="m-t">
                              {RichText.asText(content.intro__text__left)}
                            </p>
                          </Col>
                        )}
                      {content.intro__text__right &&
                        RichText.asText(content.intro__text__right) !== '' && (
                          <Col sm={6}>
                            <p className="m-t">
                              {RichText.asText(content.intro__text__right)}
                            </p>
                          </Col>
                        )}
                    </Row>
                  </TextContainer>
                </Col>
              )}
            </Row>
          </Container>
        </Section>
      )}

      <MapBoxSection data={map} />

      {content.image__car && content.image__car.url && (
        <Section>
          <Container fluid>
            <ImageComponent
              src={content.image__car.url}
              alt={content.image__car.alt}
              width={content.image__car.dimensions.width}
              height={content.image__car.dimensions.height}
              offset="full"
            />
          </Container>
        </Section>
      )}

      {(RichText.asText(content.title__community) !== '' ||
        RichText.asText(content.text__community) !== '') && (
        <Section>
          <Container fluid>
            {content.title__community &&
              RichText.asText(content.title__community) !== '' && (
                <h2 className="font--xxl font--primary">
                  {RichText.asText(content.title__community)}
                </h2>
              )}
            {content.text__community &&
              RichText.asText(content.text__community) !== '' && (
                <Row>
                  <Col
                    md={{
                      size: 6,
                      offset: 6,
                    }}
                    lg={{
                      size: 4,
                      offset: 8,
                    }}
                  >
                    <p>
                      {content.text__community &&
                        RichText.asText(content.text__community)}
                    </p>
                  </Col>
                </Row>
              )}
          </Container>
        </Section>
      )}

      {content.image__pool && content.image__pool.url && (
        <Section hasBottomMobile={false}>
          <Container fluid>
            <Row>
              <Col md={8}>
                <div className="m-t-image">
                  <ImageComponent
                    src={content.image__pool.url}
                    alt={content.image__pool.alt}
                    alignment="horizontal"
                    offset="left-full"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {content.image__skatepark && content.image__skatepark.url && (
        <Section hasBottomMobile={false}>
          <Container fluid>
            <Row>
              <Col
                md={{
                  size: 10,
                  offset: 2,
                }}
              >
                <div className="m-t-image">
                  <ImageComponent
                    src={content.image__skatepark.url}
                    alt={content.image__skatepark.alt}
                    alignment="horizontal"
                    offset="right-full"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {((content.image__cafe && content.image__cafe.url) ||
        (content.text__diversity &&
          RichText.asText(content.text__diversity) !== '') ||
        (content.text__life && RichText.asText(content.text__life) !== '') ||
        (content.image__restaurant && content.image__restaurant.url)) && (
        <Section hasBottomMobile={false}>
          <Container fluid>
            <Row>
              <Col md={6}>
                <Row className="full-height flex--align-space-between">
                  {content.image__cafe && content.image__cafe.url && (
                    <Col md={{ size: 12 }} lg={{ size: 8 }}>
                      <div className="m-t-image">
                        <ImageComponent
                          src={content.image__cafe.url}
                          alt={content.image__cafe.alt}
                          alignment="vertical"
                          offset="left-full"
                        />
                      </div>
                    </Col>
                  )}
                  {RichText.asText(content.text__diversity) !== '' && (
                    <Col
                      md={{
                        size: 12,
                        offset: 0,
                      }}
                      lg={{
                        size: 6,
                        offset: 1,
                      }}
                      className="m-t hide--sm"
                    >
                      {RichText.render(content.text__diversity)}
                    </Col>
                  )}
                </Row>
              </Col>
              <Col md={6}>
                <Row>
                  {RichText.asText(content.text__life) !== '' && (
                    <Col
                      md={{
                        size: 12,
                        offset: 0,
                      }}
                      lg={{
                        size: 6,
                        offset: 6,
                      }}
                      className="m-t--sm"
                    >
                      {RichText.render(content.text__life)}
                      <div className="m-t--sm show--sm">
                        {RichText.render(content.text__diversity)}
                      </div>
                    </Col>
                  )}
                  {content.image__restaurant &&
                    content.image__restaurant.url && (
                      <Col className="m-t">
                        <div className="m-t-image">
                          <ImageComponent
                            src={content.image__restaurant.url}
                            alt={content.image__restaurant.alt}
                            alignment="vertical"
                            offset="right"
                          />
                        </div>
                      </Col>
                    )}
                </Row>
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {((content.image__tennis && content.image__tennis.url) ||
        (content.image__bridge && content.image__bridge.url) ||
        (content.image__facade && content.image__facade.url) ||
        (content.image__statue && content.image__statue.url) ||
        RichText.asText(content.text__astoria_park) !== '') && (
        <Section>
          <Container fluid>
            <Row>
              <Col md={6}>
                {content.image__tennis && content.image__tennis.url && (
                  <div className="m-t-image">
                    <ImageComponent
                      src={content.image__tennis.url}
                      alt={content.image__tennis.alt}
                      alignment="vertical"
                      offset="left-full"
                    />
                  </div>
                )}
                {content.image__bridge && content.image__bridge.url && (
                  <div className="m-t-image">
                    <ImageComponent
                      src={content.image__bridge.url}
                      alt={content.image__bridge.alt}
                      alignment="vertical"
                      offset="left-full"
                    />
                  </div>
                )}
              </Col>
              <Col md={6} className="m-t-image--md">
                <Row className="flex-column-reverse--sm">
                  <Col
                    md={{
                      size: 6,
                      offset: 3,
                    }}
                  >
                    {content.image__facade && content.image__facade.url && (
                      <div className="m-t--sm">
                        <ImageComponent
                          src={content.image__facade.url}
                          alt={content.image__facade.alt}
                          alignment="vertical"
                        />
                      </div>
                    )}
                    {content.image__statue && content.image__statue.url && (
                      <div className="m-t-image">
                        <ImageComponent
                          src={content.image__statue.url}
                          alt={content.image__statue.alt}
                          alignment="vertical"
                        />
                      </div>
                    )}
                  </Col>
                  {RichText.asText(content.text__astoria_park) !== '' && (
                    <Col md={{ size: 12 }} lg={{ size: 8 }}>
                      <div className="m-t">
                        {RichText.render(content.text__astoria_park)}
                      </div>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {(RichText.asText(content.text__arts_culture) !== '' ||
        (content.image__beach && content.image__beach.url)) && (
        <Section>
          <Container fluid>
            <Row>
              {RichText.asText(content.text__arts_culture) !== '' && (
                <Col md={{ size: 6 }} lg={{ size: 4 }}>
                  <div className="full-height flex--align-center">
                    {RichText.render(content.text__arts_culture)}
                  </div>
                </Col>
              )}
              {content.image__beach && content.image__beach.url && (
                <Col
                  md={{ size: 6 }}
                  lg={{
                    size: 4,
                    offset: 4,
                  }}
                  className="m-t--sm"
                >
                  <ImageComponent
                    src={content.image__beach.url}
                    alt={content.image__beach.alt}
                    alignment="vertical"
                    offset="right"
                  />
                </Col>
              )}
            </Row>
          </Container>
        </Section>
      )}

      {RichText.asText(content.title__map) !== '' && (
        <Section>
          <Anchor id="transportation" />
          <Container fluid>
            <Row>
              <Col xl={{ size: 10, offset: 1 }}>
                <h2 className="font--xxl font--primary">
                  {RichText.asText(content.title__map)}
                </h2>
                <Button
                  title="View transportation info"
                  href="https://prismic-io.s3.amazonaws.com/astoria-west/95371922-fef9-477f-93f1-ce7fdffbbde9__22.11.11_AW.Public.Transport.Maps.Flyer.FINAL.pdf"
                  targetExternal={true}
                />
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {content.image__map && content.image__map.url && (
        <Section>
          <Container fluid>
            <ImageComponent
              src={content.image__map.url}
              alt={content.image__map.alt}
              width={content.image__map.dimensions.width * 2}
              height={content.image__map.dimensions.height * 2}
              offset="full"
              objectFit="contain"
            />
          </Container>
        </Section>
      )}

      {RichText.asText(content.text__map) !== '' && (
        <Section>
          <Container fluid>
            <Row>
              <Col md={{ size: 6 }} lg={{ size: 4 }}>
                {RichText.render(content.text__map)}
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {content.image__guide && content.image__guide.url && (
        <Section>
          <Container fluid>
            <Row className="flex-column-reverse--sm">
              {content.image__guide && content.image__guide.url && (
                <Col md={{ size: 4 }}>
                  <div className="m-t--sm">
                    <ImageComponent
                      src={content.image__guide.url}
                      alt={content.image__guide.alt}
                      width={content.image__guide.dimensions.width}
                      height={content.image__guide.dimensions.height}
                      offset="left-full"
                    />
                  </div>
                </Col>
              )}
              <Col md={{ size: 8 }}>
                <h3 className="font--xxl font--primary ">
                  Neighborhood <br />
                  Guide
                </h3>
                <div>
                  {((content.link__guide__view &&
                    content.link__guide__view.url) ||
                    (content.link__guide__download &&
                      content.link__guide__download.url)) && (
                    <div className="button-container">
                      {content.link__guide__view &&
                        content.link__guide__view.url && (
                          <Button
                            title="View"
                            outline="outline-dark"
                            href={content.link__guide__view.url}
                            targetExternal={content.link__guide__view.target}
                          />
                        )}
                      {content.link__guide__download &&
                        content.link__guide__download.url && (
                          <Button
                            title="Download"
                            outline="outline-dark"
                            href={content.link__guide__download.url}
                            targetExternal={
                              content.link__guide__download.target
                            }
                          />
                        )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </Section>
      )}

      {(RichText.asText(content.text__quote) !== '' ||
        RichText.asText(content.text__name) ||
        (content.image__ryan && content.image__ryan.url)) && (
        <Section hasBottomMobile={false}>
          <Container fluid>
            <Row>
              {RichText.asText(content.text__quote) !== '' && (
                <Col md={{ size: 4 }}>
                  <h3 className="font--lg font--secondary font--bold m-t--sm">
                    {RichText.asText(content.text__quote)}
                  </h3>
                  <p>{RichText.asText(content.text__name)}</p>
                </Col>
              )}
              {content.image__ryan && content.image__ryan.url && (
                <Col
                  md={{
                    size: 4,
                    offset: 4,
                  }}
                >
                  <div className="m-t--sm">
                    <ImageComponent
                      src={content.image__ryan.url}
                      alt={content.image__ryan.alt}
                      alignment="square"
                      offset="right-full"
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Container>
        </Section>
      )}

      {((content.image__bela && content.image__bela.url) ||
        content.links.length > 0 ||
        RichText.asText(content.text__credits) !== '' ||
        RichText.asText(content.text__ryan) !== '') && (
        <Section>
          <Container fluid>
            <Row>
              {content.image__bela && content.image__bela.url && (
                <Col md={{ size: 6 }} lg={{ size: 4 }}>
                  <div className="m-t-image--sm">
                    <ImageComponent
                      src={content.image__bela.url}
                      alt={content.image__bela.alt}
                      alignment="vertical"
                    />
                  </div>
                  {content.links.length > 0 && (
                    <div className="m-t">
                      <LinkTree links={content.links} />
                    </div>
                  )}
                </Col>
              )}
              {(RichText.asText(content.text__credits) !== '' ||
                RichText.asText(content.text__ryan) !== '') && (
                <Col
                  md={{ size: 6 }}
                  lg={{
                    size: 4,
                    offset: 4,
                  }}
                >
                  <div className="m-t--sm">
                    {RichText.asText(content.text__credits) !== '' &&
                      RichText.render(content.text__credits)}
                    {RichText.asText(content.text__ryan) !== '' &&
                      RichText.render(content.text__ryan)}
                  </div>
                </Col>
              )}
            </Row>
          </Container>
        </Section>
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const res = await Client.query(
    '[any(document.type, ["neighbourhood", "neighborhood_map"])]'
  );

  // Filter to get the content for the neighborhood page and mapbox
  const pageContent = res.results.filter(
    (page: { type: string }) => page.type === 'neighbourhood'
  );
  const mapContent = res.results.filter(
    (page: { type: string }) => page.type === 'neighborhood_map'
  );

  const content = pageContent[0].data;
  const map = mapContent[0].data;

  return {
    props: {
      content,
      map,
    },
  };
};

export default Neighborhood;
